/**
 * State Style01 - 標準資訊卡：icon + label + value + lifecycle
 */
import type { StateControlProps, StateLifecycle } from './stateStyleTypes'
import {
  formatStateValue,
  getStateConfig,
  getStateLifecycleColor,
  inferStateLifecycle,
} from './traitStyleConfig'
import './StateStyle01.css'

const LIFECYCLE_LABEL: Record<StateLifecycle, string> = {
  live: 'Live',
  warn: 'Warn',
  error: 'Error',
  stale: 'Stale',
  offline: 'Offline',
}

export function StateStyle01({
  stateKey,
  value,
  unit = '',
  label,
  icon,
  lifecycle,
  updatedAtLabel,
  precision,
  thresholdEnabled,
  disabled = false,
}: StateControlProps) {
  const config = getStateConfig(stateKey, { icon })
  const displayValue = formatStateValue(value, precision)
  const displayLabel = label ?? config.label ?? stateKey
  const resolvedLifecycle =
    lifecycle ??
    (disabled
      ? 'offline'
      : inferStateLifecycle(stateKey, value, {
          thresholdEnabled,
        }))
  const lifecycleColor = getStateLifecycleColor(resolvedLifecycle)
  const lifecycleText = LIFECYCLE_LABEL[resolvedLifecycle]
  const updatedLabel = String(updatedAtLabel || '2m 前更新')

  return (
    <div
      className="trait-state-style01 trait-stitch-glass"
      data-css-slot="stateContainer"
      data-css-slot-label="State Container"
      data-css-state={resolvedLifecycle}
    >
      <div className="trait-state-style01__left">
        <span
          className="trait-state-style01__icon"
          style={{ color: lifecycleColor }}
          data-css-slot="stateIcon"
          data-css-slot-label="State Icon"
          data-css-state={resolvedLifecycle}
        >
        <span className="material-symbols-rounded">{config.icon}</span>
      </span>
        <div className="trait-state-style01__meta">
          <span
            className="trait-state-style01__label"
            data-css-slot="stateLabel"
            data-css-slot-label="State Label"
            data-css-state={resolvedLifecycle}
          >
            {displayLabel}
          </span>
          <span
            className="trait-state-style01__updated"
            data-css-slot="stateUpdatedAt"
            data-css-slot-label="State Updated Time"
            data-css-state={resolvedLifecycle}
          >
            {updatedLabel}
          </span>
        </div>
      </div>
      <div className="trait-state-style01__right">
        <div className="trait-state-style01__value-wrap">
          <span
            className="trait-state-style01__value"
            style={{ color: config.color }}
            data-css-slot="stateValue"
            data-css-slot-label="State Value"
            data-css-state={resolvedLifecycle}
          >
            {displayValue}
          </span>
          {unit ? (
            <span
              className="trait-state-style01__unit"
              data-css-slot="stateUnit"
              data-css-slot-label="State Unit"
              data-css-state={resolvedLifecycle}
            >
              {unit}
            </span>
          ) : null}
        </div>
        <span
          className="trait-state-style01__badge"
          style={{ '--state-lifecycle-color': lifecycleColor } as React.CSSProperties}
          data-css-slot="stateBadge"
          data-css-slot-label="State Lifecycle Badge"
          data-css-state={resolvedLifecycle}
        >
          {lifecycleText}
        </span>
      </div>
    </div>
  )
}
